import { Box, IconButton, Avatar, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowBack, KeyboardArrowDown } from '@mui/icons-material';
import { useL10n, useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { LogOutIcon } from '@uplift/icons';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../Button';
import { IconWithText } from '../../Icons';
import { ClosableMenu } from '../../lib/simple-menu';
import { AutoLogoutTrigger } from '../../molecules';
import { NavigationExternalLink, NavigationProfileMenuItem } from '../components';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';

export type ContextualNavigationBackButtonProps = {
  text?: React.ReactNode;
  to?: string;
  anchor?: boolean;
  newWindow?: boolean;
};

const useBackButtonStyles = makeStyles()({
  headerButton: {
    padding: 0
  },
});

export const ContextualNavigationBackButton: React.VFC<ContextualNavigationBackButtonProps> = ({ text, anchor, newWindow, to = '/main' }) => {
  const l10n = useL10n();
  const { classes } = useBackButtonStyles();

  if (anchor) {
    return (
      <Button
        type="button"
        startIcon={<ArrowBack />}
        className={classes.headerButton}
        component="a"
        href={to}
        target={newWindow ? '_blank' : undefined}
        noreferrer={newWindow ? true : undefined}
      >
        {text ?? l10n.navigation.back}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      startIcon={<ArrowBack />}
      className={classes.headerButton}
      component={Link}
      to={to}
    >
      {text ?? l10n.navigation.back}
    </Button>
  );
};

const useLogOutStyles = makeStyles({ name: 'contextual-navigation-logout' })({
  avatar: {
    width: '28px',
    height: '28px',
  },
  logOut: {
    cursor: 'pointer',
  },
  menuList: {
    marginTop: '3px'
  },
});

export type ContextualNavigationLogOutProps = {
  name?: string;
  avatarSrc?: string;
};

export const ContextualNavigationLogOut: React.FC<ContextualNavigationLogOutProps> = props => {
  const timezone = useTimezone();
  const currentYear = CalendarDateService.date().tz(timezone).year();
  const { classes } = useLogOutStyles();
  const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget.parentElement);
  }, []);

  const button = useMemo(() => {
    if (props.name) {
      return (
        <Typography onClick={handleClick} className={classes.logOut}>
          <IconWithText icon={<KeyboardArrowDown />} placement="end">
            {props.name}
          </IconWithText>
        </Typography>
      );
    }

    return (
      <IconButton size="small" onClick={handleClick}>
        <Avatar className={classes.avatar} src={props.avatarSrc} alt="" />
      </IconButton>
    );
  }, [ classes.avatar, classes.logOut, handleClick, props.avatarSrc, props.name ]);

  return (
    <Box display="flex" alignItems="center">
      {button}
      <ClosableMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        PopoverClasses={{ root: classes.menuList }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {props.children}
        <NavigationProfileMenuItem label="Log Out" icon={LogOutIcon} onClick={() => logoutTrigger()} />
        <Box px={2} pt={1}>
          <Typography variant="body2" color="textSecondary" style={{ fontSize: '12px' }}>
            <NavigationExternalLink href="https://www.joinuplift.co/tos" data-testid="nav-profile-terms">Terms</NavigationExternalLink>
              · <NavigationExternalLink href="https://www.joinuplift.co/privacy" data-testid="nav-profile-privacy">Privacy</NavigationExternalLink>
              · UpLift © {currentYear}
          </Typography>
        </Box>
      </ClosableMenu>
    </Box>
  );
};
